import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import { SubSink } from "subsink";
import { AuthService } from "../../../auth/auth.service";
import { MessagingService } from "../../../core/messaging/messaging.service";
import { SeverityType } from "../../../core/messaging/severity-type.enum";
import { FormService } from "../../../dynamic-forms/form.service";
import { Autocomplete } from "../../../dynamic-forms/inputs/autocomplete/autocomplete.model";
import { ChipAutocomplete } from "../../../dynamic-forms/inputs/chip-autocomplete/chip-autocomplete.model";
import { Dropdown } from "../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { SelectableInput } from "../../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../../dynamic-forms/inputs/textbox/textbox.model";
import { Tags } from "../../../platform/modules/service-org-admin/service-org-config/model/Tags.model";
import { ServiceOrgConfigComponent } from "../../../platform/modules/service-org-admin/service-org-config/service-org-config.component";
import { ArrayHelper } from "../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { TagActionType } from "../model/tag-action-type.enum";
import { TagItems } from "../model/tag-items.model";
import { TagSourceType } from "../model/tag-source-type.enum";
import { TagType } from "../model/tag-type.enum";
import { TagService } from "../tag.service";
@Component({
  selector: "app-tag-manage",
  templateUrl: "./tag-manage.component.html",
  styleUrls: ["./tag-manage.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagManageComponent implements OnInit, OnDestroy {
  @ViewChild(ServiceOrgConfigComponent) serviceOrg: ServiceOrgConfigComponent;
  @Input() entityName: string;
  manageTagForm: FormGroup;
  tabManagement: FormGroup;
  tagActionInput: Dropdown;
  tagTypeInput: Dropdown;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<null>(true);
  @Output() onUpdate = new EventEmitter<null>(true);
  @Output() tagGridUpdate: EventEmitter<void> = new EventEmitter();
  hasAddActionType = false;
  hasReplaceActionType = false;
  @Input() tagType: TagType;
  @Input() createTag: boolean;
  @Input() editTag: boolean;
  @Input() deleteTag: boolean;
  @Input() headerSet: string;
  @Input() replaceTag: boolean;
  deleteTagInput: Textbox;

  @Input() tagAction: TagActionType;
  currentTagInput: Textbox;
  replaceTagInput: ChipAutocomplete;
  tagAutoInput: Autocomplete;
  tagItems: TagItems;
  tagInput: Textbox;
  hasRemoveActionType = false;
  tagId: number;
  tagName: string;
  color: string;
  tagSearch: string;
  isCreateTagTemplateVisible: boolean;
  sink = new SubSink();
  searchTextChanged = new Subject<string>();

  private pVisible = false;
  @Input()
  get visible(): boolean {
    return this.pVisible;
  }
  set visible(value: boolean) {
    if (this.tagObj) {
      this.formLoad();
      this.tabManagement.get("tag").setValue(this.tagObj.tagText);
      this.tabManagement.get("deleteTag").setValue(this.tagObj.tagText);
      this.manageTagForm.get("currentTag").setValue(this.tagObj.tagText);
    } else {
      if (this.tabManagement) {
        this.tabManagement.get("tag").reset();
        this.tabManagement.get(this.tagTypeInput.key).reset();
      }

    }
    this.isCreateTagTemplateVisible = false;
    this.pVisible = value;
    this.visibleChange.emit(this.visible);
    this.getTagActions();

    if (!this.visible) {
      this.closing();
    }
  }

  private tagObj: Tags;
  @Input()
  get editTagObj(): any {
    return this.tagObj;
  }
  set editTagObj(value: any) {
    this.tagObj = value;

  }

  private pSelectedData = [];
  @Input()
  get selectedData(): any[] {
    return this.pSelectedData;
  }
  set selectedData(value: any[]) {
    this.pSelectedData = value;
  }

  get selectedTag(): SelectableInput {
    if (this.manageTagForm == null) {
      return null;
    }

    return this.manageTagForm.get("currentTag").value || this.manageTagForm.get("removeTag").value;
  }
  set selectedTag(value: SelectableInput) {
    if (this.manageTagForm == null) {
      return;
    }

    this.manageTagForm.get("currentTag").setValue(value);
    this.manageTagForm.get("removeTag").setValue(value);
  }
  get selectedTagId(): number {
    if (this.selectedTag == null) {
      return null;
    }

    return +this.selectedTag.value;
  }

  get objectIds(): number[] {
    if (!ArrayHelper.isAvailable(this.selectedData)) {
      return [];
    }

    switch (this.tagType) {
      case TagType.CHASE:
        return this.selectedData.map(chase => +chase.chaseId);

      case TagType.MASTERDOCUMENTSOURCE:
        return this.selectedData.map(address => +address.masterDocumentSourceID);

      case TagType.DOCUMENT:
        return this.selectedData.map(document => +document.documentId);

      default:
        break;
    }

  }

  get selectedTagAction(): SelectableInput {
    if (this.manageTagForm == null) {
      return null;
    }

    return this.manageTagForm.get("tagAction").value;
  }
  set selectedTagAction(value: SelectableInput) {
    if (this.manageTagForm == null) {
      return;
    }

    this.manageTagForm.get("tagAction").setValue(value);
  }

  get selectedTagActionId(): number {
    if (this.selectedTagAction == null) {
      return null;
    }

    return +this.selectedTagAction.value;
  }


  constructor(
    private changeDetector: ChangeDetectorRef,
    private readonly formService: FormService,
    private readonly messagingService: MessagingService,
    private tagService: TagService,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.initializeTagForm();
    this.color = "#DCDCDC";

    this.sink.add(
      this.searchTextChanged
          .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() =>
            this.getTagList()
          )).subscribe()
      );
  }

  private getTagList(): void {
    const searchText = this.manageTagForm.get(this.replaceTagInput.key).value;
    this.tagService.getAllTagsList(this.tagObj.tagTypeId, null, searchText)
    .subscribe(data => {
      const sortedTagList = data;

      this.replaceTagInput.options = [...sortedTagList];
      this.changeDetector.markForCheck();
    });
  }

  private initializeTagForm(): void {
    this.tagActionInput = new Dropdown({
      key: "tagAction",
      label: "Tag Action",
      placeholder: "Select From List",
      appendTo: "body",
    });
    this.tagTypeInput = new Dropdown({
      key: "tagType",
      label: "Tag Type",
      placeholder: "Select From List",
      value: this.tagType,
      appendTo: "body",
    });
    this.tagAutoInput = new Autocomplete({
      key: "autoTag",
      label: "Tag",
      placeholder: "Type Here",
    });
    this.tagInput = new Textbox({
      key: "tag",
      label: "Tag",
      placeholder: "Type Here",
      validators: [Validators.maxLength(50)],
      errorMessages: {maxlength: "Tag must be 50 Characters or less" },
    });
    this.deleteTagInput = new Textbox({
      key: "deleteTag",
      label: "Tag",
      placeholder: "Type Here",
      readonly: true,
    });

    this.currentTagInput = new Textbox({
      key: "currentTag",
      label: "Current Tag",
      placeholder: "Type Here",
      readonly: true,
    });
    this.replaceTagInput = new ChipAutocomplete({
      key: "replaceTag",
      label: "REPLACEMENT TAG",
      placeholder: "Type Here",
      validators: [Validators.required],
      errorMessages: { required: "Field is required." },
      useFilter: false,
    });

    this.formLoad();
    this.getTagTypeActions();
  }

  formLoad() {
    this.manageTagForm = this.formService.createFormGroup([this.tagActionInput, this.currentTagInput, this.replaceTagInput]);

    this.tabManagement = this.formService.createFormGroup([this.tagTypeInput, this.tagInput, this.deleteTagInput, this.tagAutoInput]);
  }
  private getTagTypeActions(): void {
    const result = [
      new SelectableInput({ text: "Address", value: 2 }),
      new SelectableInput({ text: "Chase", value: 1 }),
      new SelectableInput({ text: "Document", value: 3 }),
    ];
    this.tagTypeInput = new Dropdown({ ...this.tagTypeInput, options: result } as any);
    this.changeDetector.markForCheck();

  }

  private getTagActions(): void {
    if (this.visible) {
      const result = [
        new SelectableInput({ text: "Add", value: 1 }),
        new SelectableInput({ text: "Remove", value: 2 }),
        new SelectableInput({ text: "Replace", value: 3 }),
      ];
      this.tagActionInput = new Dropdown({ ...this.tagActionInput, options: result } as any);
      this.changeDetector.markForCheck();
    }
  }

  private closing(): void {
    this.onHide.emit();
    this.hasAddActionType = false;
    this.hasReplaceActionType = false;
    this.hasRemoveActionType = false;
  }

  onTagConfirm(): void {
    if (this.tagAction === TagActionType.ADD) {

      this.tagItems = new TagItems({
        tagId: null,
        organizationId: this.authService.user.organizationId,
        tagTypeId: this.tagType,
        tagSourceId: TagSourceType.USER,
        tagText: this.tabManagement.get("tag").value,
        color: this.color,
        objectIds: null,
      });

      this.tagService.saveTags(this.tagItems)
        .subscribe(tagID => {
          this.tagId = tagID;
          if (this.tagId > 0) {
            this.changeDetector.markForCheck();
            this.tagGridUpdate.emit();
            this.messagingService.showToast("New Tag created successfully.", SeverityType.SUCCESS);
          }
        });

    }
    if (this.tagAction === TagActionType.EDIT) {
      this.tagItems = new TagItems({
        tagId: this.tagObj.tagId,
        organizationId: this.authService.user.organizationId,
        tagTypeId: this.tagObj.tagTypeId,
        tagSourceId: this.tagObj.tagSourceId,
        tagText: this.tabManagement.get("tag").value,
        color: this.tagObj.color,
        objectIds: null,
      });
      this.tagService.saveTags(this.tagItems)
        .subscribe(tagID => {
          this.tagId = tagID;
          if (this.tagId > 0) {
            this.changeDetector.markForCheck();
            this.tagGridUpdate.emit();
            this.messagingService.showToast("Tag edit successfully.", SeverityType.SUCCESS);
          }
        });


    }

    if (this.tagAction === TagActionType.DELETE) {
      this.tagService.deleteTags(this.tagObj.tagId)
        .subscribe(result => {

            this.messagingService.showToast("Tag deleted successfully.", SeverityType.SUCCESS);
            this.onUpdate.emit();
            this.tagGridUpdate.emit();

        });

    }
    if (this.tagAction === TagActionType.REPLACE) {
      if (this.manageTagForm.value != null) {
        this.tagItems = new TagItems({
          assignToTagId: this.manageTagForm.value.replaceTag.value,
          tagId: this.tagObj.tagId,
        });
        this.tagService.replaceTags(this.tagItems)
          .subscribe(result => {
            this.messagingService.showToast("Tag replaced successfully.", SeverityType.SUCCESS);
            this.tagGridUpdate.emit();
          });
      }
    }

    this.visible = false;
    this.changeDetector.markForCheck();
    this.formLoad();
  }

  get isCheckForm(): boolean {
    let result = true;
    if (this.tagAction === TagActionType.ADD && !this.tabManagement.invalid) {
      if (StringHelper.isAvailable(this.tabManagement.get("tag").value) &&
        NumberHelper.isAvailable(this.tabManagement.get(this.tagTypeInput.key).value)) {
        result = false;
      }

    }
    if (this.tagAction === TagActionType.EDIT && !this.tabManagement.invalid) {
      if (StringHelper.isAvailable(this.tabManagement.get("tag").value)) {
        result = false;
      }
    }
    if (this.tagAction === TagActionType.DELETE) {
      if (StringHelper.isAvailable(this.tabManagement.get("deleteTag").value)) {
        result = false;
      }
    }

    if (this.tagAction === TagActionType.REPLACE
        && !this.manageTagForm.invalid
        && this.manageTagForm.get(this.replaceTagInput.key).value
        && this.manageTagForm.get(this.replaceTagInput.key).value.text) {

        if (this.manageTagForm.get(this.replaceTagInput.key).value.text.toLowerCase() === this.tagSearch) {
          result = false;
        }

    }

    return result;
  }

  getTagType(data: any): void {
    this.tagType = data.value;
  }
  onSelectedColor(selectedTagColor: string): void {
    this.tagName = null;
    this.isCreateTagTemplateVisible = false;
    this.color = selectedTagColor;
  }

  getTagActionType(data: any): void {
    if (data.value === TagActionType.ADD) {
      this.tagAction = TagActionType.ADD;
      this.hasAddActionType = true;
      this.hasReplaceActionType = false;
      this.hasRemoveActionType = false;
    }
    if (data.value === TagActionType.REMOVE) {
      this.tagAction = TagActionType.REMOVE;
      this.hasAddActionType = false;
      this.hasReplaceActionType = false;
      this.hasRemoveActionType = true;
    }
    if (data.value === TagActionType.REPLACE) {
      this.tagAction = TagActionType.REPLACE;
      this.hasAddActionType = true;
      this.hasReplaceActionType = true;
      this.hasRemoveActionType = false;
    }
  }
  onInputSearchTag(event: any): void {
    if (event.target) {
      this.tagSearch = event.target.value.toLowerCase();
    }

  }
  onChangeTag(event: any): void {
    this.tagSearch = event.text.toLowerCase();
  }

  onkeyUp(event): void {
    this.searchTextChanged.next(event.target.value);
  }

  onSearchChange(value) {
    this.isCreateTagTemplateVisible = StringHelper.isAvailable(value);
    this.tagName = value;
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

}
